import React, { useEffect, useState } from "react";
import {
  ChatCircleText,
  PaperPlaneTilt,
  PhoneCall,
  User,
  TrendUp,
} from "@phosphor-icons/react";
import { fetchLeadsSummary } from "@/lib/api";

const TILES = [
  {
    key: "contacted",
    label: "Contacted",
    icon: ChatCircleText,
    tone: "text-[#002DFF]",
    bg: "bg-electric-soft",
  },
  {
    key: "proposal_sent",
    label: "Proposals sent",
    icon: PaperPlaneTilt,
    tone: "text-[#92400E]",
    bg: "bg-[#FEF3C7]",
  },
  {
    key: "call_back",
    label: "Callbacks set",
    icon: PhoneCall,
    tone: "text-[#5B21B6]",
    bg: "bg-[#EDE0FF]",
  },
  {
    key: "not_contacted",
    label: "Reset to open",
    icon: User,
    tone: "text-[#495057]",
    bg: "bg-[#F1F3F5]",
  },
];

export function DailySummary({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchLeadsSummary()
      .then((d) => !cancelled && setData(d))
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  if (loading) {
    return (
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-lg gbp-shimmer" />
        ))}
      </div>
    );
  }
  if (!data) return null;

  const trend = data.trend || [];
  const maxTotal = Math.max(1, ...trend.map((t) => t.total || 0));
  const weekTotal = trend.reduce((s, d) => s + (d.total || 0), 0);

  return (
    <section
      data-testid="daily-summary"
      className="mb-6 overflow-hidden rounded-lg border border-border bg-white"
    >
      <div className="flex flex-col gap-1 border-b border-border/60 px-4 py-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="font-display text-lg font-bold text-ink">
            Today&apos;s activity
          </div>
          <div className="font-sans text-xs text-muted-foreground">
            {data.today_total || 0} lead update{data.today_total === 1 ? "" : "s"}{" "}
            logged today ·{" "}
            <span className="font-mono">{data.operator_tz || "UTC"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          <TrendUp size={12} weight="bold" />
          {weekTotal} this week
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-4">
        {TILES.map((t) => {
          const Icon = t.icon;
          const todayN = data.today?.[t.key] ?? 0;
          const allN = data.all_time?.[t.key] ?? 0;
          return (
            <div
              key={t.key}
              data-testid={`summary-tile-${t.key}`}
              className="rounded-md border border-border bg-white p-3"
            >
              <div className="mb-1 flex items-center justify-between">
                <div
                  className={`grid h-6 w-6 place-items-center rounded ${t.bg} ${t.tone}`}
                >
                  <Icon size={12} weight="fill" />
                </div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  today
                </span>
              </div>
              <div className={`font-display text-3xl font-bold tabular-nums ${t.tone}`}>
                {todayN}
              </div>
              <div className="mt-1 font-sans text-xs text-muted-foreground">
                {t.label}
              </div>
              <div className="mt-2 border-t border-border/60 pt-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {allN} total
              </div>
            </div>
          );
        })}
      </div>

      {weekTotal > 0 ? (
        <div className="border-t border-border/60 bg-surface/40 px-4 py-3">
          <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            Last 7 days
          </div>
          <div className="flex items-end gap-2">
            {trend.map((day) => {
              const heightPct = Math.round(((day.total || 0) / maxTotal) * 100);
              return (
                <div
                  key={day.date}
                  className="flex flex-1 flex-col items-center gap-1"
                  data-testid={`trend-day-${day.date}`}
                  title={`${day.date} · ${day.total} update(s)`}
                >
                  <div className="flex h-16 w-full items-end">
                    <div
                      className={`w-full rounded-t ${
                        day.is_today ? "bg-electric" : "bg-electric/40"
                      }`}
                      style={{ height: `${Math.max(4, heightPct)}%` }}
                    />
                  </div>
                  <div
                    className={`font-mono text-[10px] uppercase tracking-wider ${
                      day.is_today ? "font-bold text-electric" : "text-muted-foreground"
                    }`}
                  >
                    {day.label}
                  </div>
                  <div className="font-mono text-[10px] tabular-nums text-muted-foreground">
                    {day.total || 0}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
}
